/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useServiceContext } from '../../root'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'
import { Formik, Field, ErrorMessage } from 'formik'
import Loader from '@mdsol/lego/lib/Loader'
import { Modal } from '../Modal'
import type { DetailedWorkflow } from '../../services/api/workflows'
import { WorkflowStepType } from '../../screens/tasks/types'
import { useEsignature } from '../../hooks/useEsignature'
import { getDatePickerFormat } from '~/utils/dateFormatting'

interface WorkflowEffectiveDateModalProps {
  workflowId: number
  workflow?: DetailedWorkflow
  onSuccess?: () => void
}

export function WorkflowEffectiveDateModal({
  workflowId,
  workflow: workflowProp,
  onSuccess
}: WorkflowEffectiveDateModalProps) {
  const context = useServiceContext()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { signAction } = useEsignature()
  const [isOpen, setIsOpen] = useState(false)

  const [workflow, setWorkflow] = useState<DetailedWorkflow | null>(workflowProp || null)
  const [currentEffectiveDate, setCurrentEffectiveDate] = useState<string>('')
  const [effectiveDateTaskId, setEffectiveDateTaskId] = useState<string | null>(null)
  const [loading, setLoading] = useState(!workflowProp)
  const [initialValues, setInitialValues] = useState({
    newEffectiveDate: null as Date | null,
    newEffectiveTime: null as Date | null,
    makeEffectiveImmediately: false
  })

  useEffect(() => {
    setIsOpen(true)
    if (!workflowProp) {
      loadWorkflow()
    } else {
      checkTrainingPhase(workflowProp)
    }
  }, [])

  const loadWorkflow = async () => {
    try {
      const data = await context.api.workflows.getWorkflow(workflowId)
      setWorkflow(data)
      checkTrainingPhase(data)
    } catch (error) {
      console.error('Failed to load workflow:', error)
      context.service.toast.error(t('ERROR_LOADING_WORKFLOW') || 'Failed to load workflow')
      handleClose()
    } finally {
      setLoading(false)
    }
  }

  const checkTrainingPhase = (wf: DetailedWorkflow) => {
    // Find the ACTIVE workflow step with type TRAINING
    // Note: WorkflowStep uses 'type' and 'status' properties (not 'workflowStepType' and 'stepStatus')
    const trainingStep = wf.workflowSteps?.find(
      (step: any) => step.type === WorkflowStepType.TRAINING && step.status === 'ACTIVE'
    )

    if (!trainingStep || !trainingStep.effectiveDateTaskId) {
      context.service.toast.error(t('SOMETHING_WENT_WRONG_MSG_ERROR') || 'Something went wrong')
      console.error('Current workflow phase type is not TRAINING')
      handleClose()
      return
    }

    setEffectiveDateTaskId(String(trainingStep.effectiveDateTaskId))

    // Set current effective date for display
    if (trainingStep.effectiveDate) {
      const effectiveDate = new Date(trainingStep.effectiveDate)
      setCurrentEffectiveDate(context.service.filter.date(effectiveDate, true))

      // Initialize form with current effective date
      setInitialValues({
        newEffectiveDate: new Date(trainingStep.effectiveDate),
        newEffectiveTime: new Date(trainingStep.effectiveDate),
        makeEffectiveImmediately: false
      })
    }

    setLoading(false)
  }

  const handleClose = () => {
    setIsOpen(false)
    if (onSuccess) {
      onSuccess()
    }
  }

  const handleSubmit = async (values: {
    newEffectiveDate: Date | null
    newEffectiveTime: Date | null
    makeEffectiveImmediately: boolean
  }) => {
    if (!effectiveDateTaskId) {
      context.service.toast.error(t('ERROR_NO_EFFECTIVE_DATE_TASK') || 'No effective date task found')
      return
    }

    // Close modal first (like legacy)
    handleClose()

    try {
      // Prepare the e-signature message
      const esignMessage =
        t('ADMIN_ACTION', {
          action: t('ADMIN_ACTION_UPDATE_EFFECTIVE_DATE') || 'Update Effective Date'
        }) || 'Admin Action: Update Effective Date'

      // Get e-signature validation - signAction expects (actionType, actionName, options)
      let validation
      try {
        validation = await signAction('CHANGE_EFFECTIVE_DATE', 'ADMIN_ACTION_UPDATE_EFFECTIVE_DATE', {
          esignMsg: esignMessage
        })
      } catch (error) {
        // User cancelled e-signature
        return
      }

      // Combine date and time into a single timestamp
      let timestamp: number | null = null
      if (!values.makeEffectiveImmediately && values.newEffectiveDate && values.newEffectiveTime) {
        const combined = new Date(values.newEffectiveDate)
        combined.setHours(values.newEffectiveTime.getHours())
        combined.setMinutes(values.newEffectiveTime.getMinutes())
        combined.setSeconds(0)
        combined.setMilliseconds(0)
        timestamp = combined.getTime()
      }

      const isDSO = validation && validation.username && validation.password
      const isSSO = validation && validation.result && validation.result.esignature === 'verified'

      // Convert ValidationResult to ESignature format (adds required index signature)
      if (!isDSO && !isSSO) {
        console.error('E-signature was not provided or cancelled')
        context.service.toast.error('E-signature was not provided or cancelled')
        return
      }
      const esignature: Record<string, unknown> & { username?: string; password?: string; reason?: string } = {
        relayState: validation.relayState
      }

      // Call API
      await context.api.tasks.changeEffectiveDate(
        effectiveDateTaskId,
        timestamp ?? undefined,
        values.makeEffectiveImmediately,
        esignature
      )

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] })
      queryClient.invalidateQueries({ queryKey: ['tasks'] })

      context.service.toast.success(
        t('WORKFLOW_CHANGE_EFFECTIVE_DATE_SUCCESS_MSG') || 'Effective date updated successfully'
      )
    } catch (error: any) {
      console.error('Failed to update effective date:', error)
      // Check if error is from e-signature cancellation
      if (error?.message === 'E-signature cancelled or failed') {
        // Don't show error toast for user cancellation
        return
      }
      const errorMessage =
        error?.data?.message || error?.body?.message || error?.message || 'Failed to update effective date'
      context.service.toast.error(errorMessage)
    }
  }

  if (loading) {
    return (
      <Modal
        show={true}
        handleClose={handleClose}
        title={t('UPDATE_EFFECTIVE_DATE_AND_TIME') || 'Update Effective Date'}
        size="lg"
      >
        <Loader blocking={true}>
          <Modal.Body>
            <div style={{ minHeight: '200px' }} />
          </Modal.Body>
        </Loader>
      </Modal>
    )
  }

  if (!workflow || !effectiveDateTaskId) {
    return null
  }

  return (
    <Modal
      className="theme-sandman"
      show={isOpen}
      handleClose={handleClose}
      title={t('UPDATE_EFFECTIVE_DATE_AND_TIME') || 'Update Effective Date'}
      size="lg"
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validate={(values) => {
          const errors: { newEffectiveDate?: string; newEffectiveTime?: string } = {}

          if (!values.makeEffectiveImmediately) {
            if (!values.newEffectiveDate) {
              errors.newEffectiveDate = t('REQUIRED') || 'Required'
            }
            if (!values.newEffectiveTime) {
              errors.newEffectiveTime = t('REQUIRED') || 'Required'
            }
          }

          return errors
        }}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, handleSubmit, dirty }) => (
          <>
            <Loader blocking={loading}>
              <Modal.Body>
                <div className="form-group wparag">
                  <label className="control-label">{t('EXISTING_EFFECTIVE_DATE') || 'Existing Effective Date'}</label>
                  <p className="whitespaceprewrap form-control-static">
                    {currentEffectiveDate || t('NOT_SET') || 'Not Set'}
                  </p>
                </div>

                <div className="form-group">
                  <label className="control-label">{t('NEW_EFFECTIVE_DATE') || 'New Effective Date'}</label>

                  <div className="form-group w100p checkbox" style={{ marginTop: '10px' }}>
                    <Field name="makeEffectiveImmediately" type="checkbox" id="makeEffectiveImmediately-checkbox">
                      {({ field }: any) => (
                        <label>
                          <input
                            {...field}
                            type="checkbox"
                            checked={values.makeEffectiveImmediately}
                            onChange={(e) => {
                              setFieldValue('makeEffectiveImmediately', e.target.checked)
                              if (e.target.checked) {
                                setFieldValue('newEffectiveDate', null)
                                setFieldValue('newEffectiveTime', null)
                              }
                            }}
                          />
                          <span className="font-weight-normal">
                            {t('MAKE_EFFECTIVE_IMMEDIATELY') || 'Make Effective Immediately'}
                          </span>
                        </label>
                      )}
                    </Field>
                  </div>

                  {!values.makeEffectiveImmediately && (
                    <>
                      <DatePicker
                        name="newEffectiveDate"
                        selected={values.newEffectiveDate}
                        onChange={(val) => setFieldValue('newEffectiveDate', val)}
                        todayButton={t('TODAY') || 'Today'}
                        dateFormatCalendar="MMMM"
                        dateFormat={getDatePickerFormat()}
                        showYearDropdown
                        className="form-control field-control"
                        id="newEffectiveDate-datepicker"
                        placeholderText={t('SELECT_DATE') || 'Select date...'}
                      />
                      <ErrorMessage name="newEffectiveDate" component="div" className="help-block text-danger" />
                    </>
                  )}
                </div>

                {!values.makeEffectiveImmediately && (
                  <div className="form-group">
                    <label className="control-label">{t('NEW_EFFECTIVE_TIME') || 'New Effective Time'}</label>
                    <DatePicker
                      selected={values.newEffectiveTime}
                      onChange={(val) => setFieldValue('newEffectiveTime', val)}
                      className="form-control field-control"
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      dateFormat="h:mm aa"
                      timeCaption={t('TIME') || 'Time'}
                      placeholderText={t('SELECT_TIME') || 'Select time...'}
                    />
                    <ErrorMessage name="newEffectiveTime" component="div" className="help-block text-danger" />
                  </div>
                )}
              </Modal.Body>
            </Loader>

            <Modal.Footer>
              <Modal.Footer.Button variant="secondary" onClick={handleClose}>
                {t('CANCEL') || 'Cancel'}
              </Modal.Footer.Button>
              <Modal.Footer.Button
                variant="primary"
                onClick={() => {
                  if (dirty) {
                    handleSubmit()
                  } else {
                    handleClose()
                  }
                }}
              >
                {t('OK') || 'OK'}
              </Modal.Footer.Button>
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default WorkflowEffectiveDateModal
